/**
 * Work Time Store (Application)
 *
 * Global work time state management using Zustand.
 * Handles work time status and related UI state.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import type { WorkTime, WorkTimeType } from '../../domain/types';

// ============================================================================
// Store Types
// ============================================================================

interface WorkTimeState {
  /** Current work time status */
  workTime: WorkTime | null;
  /** Loading state for API operations */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
}

interface WorkTimeActions {
  /**
   * Set the current work time status
   */
  setWorkTime: (workTime: WorkTime) => void;

  /**
   * Update work time type directly
   */
  updateType: (type: WorkTimeType) => void;

  /**
   * Set loading state
   */
  setLoading: (isLoading: boolean) => void;

  /**
   * Set error state
   */
  setError: (error: string | null) => void;

  /**
   * Clear all work time state
   */
  clearWorkTime: () => void;
}

type WorkTimeStore = WorkTimeState & WorkTimeActions;

// ============================================================================
// Initial State
// ============================================================================

const initialState: WorkTimeState = {
  workTime: null,
  isLoading: false,
  error: null,
};

// ============================================================================
// Store
// ============================================================================

export const useWorkTimeStore = create<WorkTimeStore>()(
  devtools(
    immer((set) => ({
      ...initialState,

      setWorkTime: (workTime) =>
        set((state) => {
          state.workTime = workTime;
          state.error = null;
        }),

      updateType: (type) =>
        set((state) => {
          if (state.workTime) {
            state.workTime.type = type;
            state.workTime.updatedAt = new Date();
          } else {
            state.workTime = {
              type,
              updatedAt: new Date(),
            };
          }
          state.error = null;
        }),

      setLoading: (isLoading) =>
        set((state) => {
          state.isLoading = isLoading;
        }),

      setError: (error) =>
        set((state) => {
          state.error = error;
          state.isLoading = false;
        }),

      clearWorkTime: () =>
        set((state) => {
          state.workTime = null;
          state.isLoading = false;
          state.error = null;
        }),
    })),
    { name: 'work-time-store' }
  )
);

// ============================================================================
// Selectors
// ============================================================================

/**
 * Get current work time type
 */
export const useWorkTimeType = () =>
  useWorkTimeStore((state) => state.workTime?.type ?? 'offline');

/**
 * Check if work time is loading
 */
export const useIsWorkTimeLoading = () =>
  useWorkTimeStore((state) => state.isLoading);

/**
 * Get work time error
 */
export const useWorkTimeError = () =>
  useWorkTimeStore((state) => state.error);
